import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Space,
  Tooltip,
  Upload,
  Collapse,
} from 'antd';
import {
  cartonApi,
  supplierApi,
  customerApi,
  productApi,
  productKey,
  parseProductKey,
  type CartonDTO,
  type CreateCartonItemInBatch,
  type CartonProductItemDTO,
  type ProductDTO,
  type ReceiveCartonRequest,
} from '../api/kfc';
import CartonDetailDrawer from '../components/CartonDetailDrawer';
import { serverTablePagination } from '../utils/tablePagination';
import { highlightText } from '../utils/highlightText';

const STATE_LABELS: Record<string, string> = {
  preparing: '待收货',
  inKFYWarehouse: '抵达凯飞越仓库',
  inAgentWarehouse: '抵达代理仓库(出发地)',
  readyForShipping: '准备运输',
  onTheWay: '在途运输',
  arrivedDstPort: '抵达目的港',
  recevied: '已签收',
};

interface BatchCartonRow {
  cartonId: string;
  manualEdited: boolean;
  products: CartonProductItemDTO[];
}

function generateCartonId(batchId: string, index: number): string {
  const base = (batchId || '').trim();
  if (!base) return '';
  return `${base}-${index + 1}`;
}

function buildCartonRows(batchId: string, boxCount: number, prev: BatchCartonRow[]): BatchCartonRow[] {
  const count = Math.max(1, boxCount);
  const rows: BatchCartonRow[] = [];
  for (let i = 0; i < count; i++) {
    const existing = prev[i];
    const autoId = generateCartonId(batchId, i);
    if (existing?.manualEdited) {
      rows.push({ ...existing });
    } else {
      rows.push({
        cartonId: autoId,
        manualEdited: false,
        products: existing?.products ?? [],
      });
    }
  }
  return rows;
}

const CartonList: React.FC = () => {
  const [list, setList] = useState<CartonDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [suppliers, setSuppliers] = useState<{ value: string; label: string }[]>([]);
  const [customers, setCustomers] = useState<{ value: string; label: string }[]>([]);
  const [productOptions, setProductOptions] = useState<ProductDTO[]>([]);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [batchForm] = Form.useForm();
  const [batchId, setBatchId] = useState('');
  const [boxCount, setBoxCount] = useState(1);
  const [cartonRows, setCartonRows] = useState<BatchCartonRow[]>([
    { cartonId: '', manualEdited: false, products: [] },
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [detailCartonId, setDetailCartonId] = useState<string | undefined>(undefined);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [receiveModalOpen, setReceiveModalOpen] = useState(false);
  const [receiveTargetIds, setReceiveTargetIds] = useState<string[]>([]);
  const [receiveSubmitting, setReceiveSubmitting] = useState(false);
  const [receiveForm] = Form.useForm();
  const [receivePictures, setReceivePictures] = useState<{ desc: string; base64: string }[]>([]);
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [supplierTargetIds, setSupplierTargetIds] = useState<string[]>([]);
  const [supplierSubmitting, setSupplierSubmitting] = useState(false);
  const [supplierForm] = Form.useForm();
  const [filterCartonIdInput, setFilterCartonIdInput] = useState('');
  const [filterShipmentIdInput, setFilterShipmentIdInput] = useState('');
  const [appliedCartonIdKeyword, setAppliedCartonIdKeyword] = useState('');
  const [appliedShipmentIdKeyword, setAppliedShipmentIdKeyword] = useState('');

  const load = useCallback((pageIndex = page) => {
    setLoading(true);
    const params: Parameters<typeof cartonApi.list>[0] = {
      page: pageIndex,
      size: pageSize,
    };
    if (appliedCartonIdKeyword) params.cartonIdKeyword = appliedCartonIdKeyword;
    if (appliedShipmentIdKeyword) params.shipmentIdKeyword = appliedShipmentIdKeyword;
    cartonApi
      .list(params)
      .then((res) => {
        const body = res.data;
        if (Array.isArray(body)) {
          setList(body);
          setTotal(body.length);
        } else {
          setList(cartonApi.unwrapList(body));
          setTotal(body?.totalElements ?? 0);
        }
      })
      .catch(() => {
        setList([]);
        setTotal(0);
        message.warning('无法加载货物列表');
      })
      .finally(() => setLoading(false));
  }, [page, pageSize, appliedCartonIdKeyword, appliedShipmentIdKeyword]);

  useEffect(() => {
    load(page);
  }, [page, pageSize, appliedCartonIdKeyword, appliedShipmentIdKeyword, load]);

  const applyFilters = () => {
    setAppliedCartonIdKeyword(filterCartonIdInput.trim());
    setAppliedShipmentIdKeyword(filterShipmentIdInput.trim());
    setPage(0);
    setSelectedRowKeys([]);
  };

  const resetFilters = () => {
    setFilterCartonIdInput('');
    setFilterShipmentIdInput('');
    setAppliedCartonIdKeyword('');
    setAppliedShipmentIdKeyword('');
    setPage(0);
    setSelectedRowKeys([]);
  };

  useEffect(() => {
    supplierApi.list().then((res) => {
      const data = (res.data || []).map((s) => ({ value: s.id!, label: `${s.supplierName || s.supplierId || s.id}` }));
      setSuppliers(data);
    }).catch(() => setSuppliers([]));
    customerApi.list().then((res) => {
      const data = (res.data || []).map((c) => ({ value: c.id!, label: `${c.name || c.customerCode || c.id}` }));
      setCustomers(data);
    }).catch(() => setCustomers([]));
  }, []);

  const ensureProductsLoaded = () => {
    if (productsLoaded || productsLoading) return;
    setProductsLoading(true);
    productApi
      .list({ view: 'summary' })
      .then((res) => {
        setProductOptions(res.data || []);
        setProductsLoaded(true);
      })
      .catch(() => setProductOptions([]))
      .finally(() => setProductsLoading(false));
  };

  const syncCartonRows = useCallback((nextBatchId: string, nextBoxCount: number) => {
    setCartonRows((prev) => buildCartonRows(nextBatchId, nextBoxCount, prev));
  }, []);

  const openDetail = (record: CartonDTO) => {
    if (!record.id) return;
    setDetailCartonId(record.id);
    setDetailDrawerOpen(true);
  };

  const closeDetail = () => {
    setDetailDrawerOpen(false);
    setDetailCartonId(undefined);
  };

  const openAdd = () => {
    setBatchId('');
    setBoxCount(1);
    setCartonRows([{ cartonId: '', manualEdited: false, products: [] }]);
    batchForm.resetFields();
    batchForm.setFieldsValue({ boxCount: 1 });
    setAddModalOpen(true);
    ensureProductsLoaded();
  };

  const onBatchIdChange = (value: string) => {
    setBatchId(value);
    syncCartonRows(value, boxCount);
  };

  const onBoxCountChange = (value: number | null) => {
    const count = value ?? 1;
    setBoxCount(count);
    syncCartonRows(batchId, count);
  };

  const onCartonIdChange = (index: number, cartonId: string) => {
    setCartonRows((prev) => {
      const next = [...prev];
      const autoId = generateCartonId(batchId, index);
      next[index] = {
        ...next[index],
        cartonId,
        manualEdited: cartonId !== autoId,
      };
      return next;
    });
  };

  const addProductToBox = (boxIndex: number, product: ProductDTO) => {
    const hscode = product.hscode;
    const nameEN = product.nameEN;
    if (!hscode || !nameEN) return;
    const key = productKey(nameEN, hscode);
    setCartonRows((prev) => {
      const next = [...prev];
      const row = next[boxIndex];
      if (!row) return prev;
      if (row.products.some((p) => productKey(p.nameEN, p.hscode) === key)) {
        message.warning('该产品已添加');
        return prev;
      }
      next[boxIndex] = {
        ...row,
        products: [...row.products, { hscode, nameEN, amount: 1 }],
      };
      return next;
    });
  };

  const removeProductFromBox = (boxIndex: number, productIndex: number) => {
    setCartonRows((prev) => {
      const next = [...prev];
      const row = next[boxIndex];
      if (!row) return prev;
      next[boxIndex] = {
        ...row,
        products: row.products.filter((_, i) => i !== productIndex),
      };
      return next;
    });
  };

  const setProductAmount = (boxIndex: number, productIndex: number, amount: number) => {
    setCartonRows((prev) => {
      const next = [...prev];
      const row = next[boxIndex];
      if (!row) return prev;
      const products = [...row.products];
      if (!products[productIndex]) return prev;
      products[productIndex] = { ...products[productIndex], amount };
      next[boxIndex] = { ...row, products };
      return next;
    });
  };

  const submitAdd = () => {
    batchForm.validateFields().then((values) => {
      const trimmedBatchId = (values.batchId || '').trim();
      const count = values.boxCount ?? 1;
      const cartons: CreateCartonItemInBatch[] = cartonRows.map((row) => ({
        cartonId: (row.cartonId || '').trim(),
        products: row.products.filter((p) => p.hscode && (p.amount ?? 0) > 0).map((p) => ({
          hscode: p.hscode,
          nameEN: p.nameEN,
          amount: p.amount,
        })),
      }));
      if (cartons.some((c) => !c.cartonId)) {
        message.warning('请填写所有箱号');
        return;
      }
      if (cartons.length !== count) {
        message.warning('箱列表数量与箱数量不一致');
        return;
      }
      setSubmitting(true);
      cartonApi
        .createCartonBatch({
          batchId: trimmedBatchId,
          boxCount: count,
          fromSupplierId: values.fromSupplierId,
          belongClientId: values.belongClientId,
          batchRemark: values.batchRemark?.trim() || undefined,
          cartons,
        })
        .then(() => {
          message.success(`成功录入批次 ${trimmedBatchId}，共 ${count} 箱，状态为待收货`);
          setAddModalOpen(false);
          load();
        })
        .catch((e) => message.error(e.response?.data?.message || '录入失败'))
        .finally(() => setSubmitting(false));
    });
  };

  const renderState = (state: string) => STATE_LABELS[state] || state;

  const openReceiveModal = (ids: string[]) => {
    const validIds = ids.filter(Boolean);
    if (validIds.length === 0) {
      message.warning('请先选择要收货的货物');
      return;
    }
    setReceiveTargetIds(validIds);
    receiveForm.resetFields();
    setReceivePictures([]);
    setReceiveModalOpen(true);
  };

  const openSupplierModal = (ids: string[]) => {
    const validIds = ids.filter(Boolean);
    if (validIds.length === 0) {
      message.warning('请先选择要修改供应商的货物');
      return;
    }
    setSupplierTargetIds(validIds);
    supplierForm.resetFields();
    setSupplierModalOpen(true);
  };

  const closeSupplierModal = () => {
    setSupplierModalOpen(false);
    setSupplierTargetIds([]);
    supplierForm.resetFields();
  };

  const submitBatchUpdateSupplier = () => {
    supplierForm.validateFields().then((values) => {
      setSupplierSubmitting(true);
      cartonApi
        .batchUpdateSupplier({
          cartonIds: supplierTargetIds,
          fromSupplierId: values.fromSupplierId,
        })
        .then(() => {
          message.success(`已更新 ${supplierTargetIds.length} 箱供应商`);
          closeSupplierModal();
          setSelectedRowKeys([]);
          load();
        })
        .catch((e) => message.error(e.response?.data?.message || '批量更新供应商失败'))
        .finally(() => setSupplierSubmitting(false));
    });
  };

  const closeReceiveModal = () => {
    setReceiveModalOpen(false);
    setReceiveTargetIds([]);
    setReceivePictures([]);
  };

  const addReceivePicture = () => {
    setReceivePictures((prev) => [...prev, { desc: '', base64: '' }]);
  };

  const removeReceivePicture = (index: number) => {
    setReceivePictures((prev) => prev.filter((_, i) => i !== index));
  };

  const setReceivePictureDesc = (index: number, desc: string) => {
    setReceivePictures((prev) => {
      const next = [...prev];
      if (!next[index]) return prev;
      next[index] = { ...next[index], desc };
      return next;
    });
  };

  const setReceivePictureBase64 = (index: number, base64: string) => {
    setReceivePictures((prev) => {
      const next = [...prev];
      if (!next[index]) return prev;
      next[index] = { ...next[index], base64 };
      return next;
    });
  };

  const submitReceive = () => {
    receiveForm.validateFields().then((values) => {
      const pictures: Record<string, string> = {};
      receivePictures.forEach((p) => {
        const desc = (p.desc || '照片').trim();
        if (p.base64) pictures[desc] = p.base64;
      });
      const body: ReceiveCartonRequest = {
        arrivedBy: values.arrivedBy,
        queryParamOfShippment: values.queryParamOfShippment?.trim() || undefined,
        pictures: Object.keys(pictures).length ? pictures : undefined,
      };
      setReceiveSubmitting(true);
      const run = () => {
        if (receiveTargetIds.length === 1) {
          return cartonApi.receive(receiveTargetIds[0], body).then((r) => [r.data]);
        }
        return cartonApi.receiveBatch({ cartonIds: receiveTargetIds, ...body }).then((r) => r.data || []);
      };
      run()
        .then(() => {
          message.success(`已收货 ${receiveTargetIds.length} 箱`);
          closeReceiveModal();
          setSelectedRowKeys([]);
          load();
        })
        .catch((e) => message.error(e.response?.data?.message || '收货失败'))
        .finally(() => setReceiveSubmitting(false));
    });
  };

  return (
    <>
      <Card
        title="货物列表"
        extra={
          <Space>
            <Button type="primary" onClick={openAdd}>
              录入货物
            </Button>
            <Button onClick={() => openReceiveModal(selectedRowKeys as string[])} disabled={selectedRowKeys.length === 0}>
              收货
            </Button>
            <Tooltip title="敬请期待">
              <Button disabled>交代理仓</Button>
            </Tooltip>
          </Space>
        }
      >
        <Space wrap style={{ marginBottom: 16 }}>
          <Input
            placeholder="模糊搜索货物标识"
            allowClear
            style={{ width: 200 }}
            value={filterCartonIdInput}
            onChange={(e) => setFilterCartonIdInput(e.target.value)}
            onPressEnter={applyFilters}
          />
          <Input
            placeholder="模糊搜索运单号"
            allowClear
            style={{ width: 200 }}
            value={filterShipmentIdInput}
            onChange={(e) => setFilterShipmentIdInput(e.target.value)}
            onPressEnter={applyFilters}
          />
          <Button type="primary" onClick={applyFilters}>查询</Button>
          <Button onClick={resetFilters}>重置</Button>
        </Space>
        {selectedRowKeys.length > 0 && (
          <Space style={{ marginBottom: 12 }}>
            <span style={{ color: '#666' }}>已选 <strong>{selectedRowKeys.length}</strong> 条</span>
            <Button size="small" onClick={() => setSelectedRowKeys([])}>清空选择</Button>
            <Button size="small" onClick={() => openReceiveModal(selectedRowKeys as string[])}>批量收货</Button>
            <Button size="small" onClick={() => openSupplierModal(selectedRowKeys as string[])}>批量改供应商</Button>
            <Tooltip title="敬请期待">
              <Button size="small" disabled>批量交代理仓</Button>
            </Tooltip>
          </Space>
        )}
        <Table
          loading={loading}
          dataSource={list}
          rowKey="id"
          scroll={{ x: 1000 }}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
          }}
          onRow={(record) => ({
            onClick: (e) => {
              if ((e.target as HTMLElement).closest('.ant-table-selection-column')) return;
              openDetail(record);
            },
            style: { cursor: 'pointer' },
          })}
          columns={[
            {
              title: '货物标识',
              dataIndex: 'cartonId',
              width: 160,
              ellipsis: true,
              render: (v) => highlightText(v, appliedCartonIdKeyword),
            },
            { title: '批次', dataIndex: 'batchId', width: 140, ellipsis: true, render: (v) => v || '-' },
            { title: '供应商', dataIndex: 'fromSupplierId', width: 120, ellipsis: true, render: (id) => suppliers.find((s) => s.value === id)?.label || id || '-' },
            { title: '客户', dataIndex: 'belongClientId', width: 120, ellipsis: true, render: (id) => customers.find((c) => c.value === id)?.label || id || '-' },
            { title: '计费重量', dataIndex: 'billingWeightKg', width: 100, render: (v: number | undefined) => (v != null ? `${v} kg` : '-') },
            { title: '状态', dataIndex: 'state', width: 120, render: (s) => renderState(s || '') },
            {
              title: '运单号',
              dataIndex: 'shipmentId',
              width: 140,
              ellipsis: true,
              render: (v) => highlightText(v, appliedShipmentIdKeyword),
            },
            { title: '创建时间', dataIndex: 'createTimestamp', width: 160, render: (t) => (t ? new Date(t).toLocaleString() : '-') },
            { title: '操作', key: 'action', width: 140, fixed: 'right', render: (_, record) => (
              <Space onClick={(e) => e.stopPropagation()}>
                <Button type="link" size="small" onClick={() => openDetail(record)}>详情</Button>
                {record.state === 'preparing' && record.id && (
                  <Button type="link" size="small" onClick={(e) => { e.stopPropagation(); openReceiveModal([record.id!]); }}>收货</Button>
                )}
                <Tooltip title="敬请期待"><Button type="link" size="small" disabled>交代理仓</Button></Tooltip>
              </Space>
            ) },
          ]}
          pagination={serverTablePagination({
            page,
            pageSize,
            total,
            onPageChange: setPage,
            onPageSizeChange: setPageSize,
          })}
        />
      </Card>

      <Modal
        title="录入货物"
        open={addModalOpen}
        onCancel={() => setAddModalOpen(false)}
        footer={null}
        width={800}
        destroyOnClose
      >
        <p style={{ marginBottom: 12, color: '#666' }}>
          按批次录入货物：填写批次标识与箱数量，系统自动生成箱号（可手动修改）。录入后状态为「待收货」。从装箱单导入功能敬请期待。
        </p>
        <Form form={batchForm} layout="vertical">
          <Form.Item name="batchId" label="批次标识" rules={[{ required: true, message: '请填写批次标识' }]}>
            <Input placeholder="如：20260712-B1" onChange={(e) => onBatchIdChange(e.target.value)} />
          </Form.Item>
          <Space style={{ display: 'flex', width: '100%' }} align="start">
            <Form.Item name="boxCount" label="箱数量" initialValue={1} rules={[{ required: true, message: '请填写箱数量' }]} style={{ flex: 1 }}>
              <InputNumber min={1} max={500} style={{ width: '100%' }} onChange={onBoxCountChange} />
            </Form.Item>
            <Form.Item name="fromSupplierId" label="供应商" rules={[{ required: true, message: '请选择供应商' }]} style={{ flex: 2 }}>
              <Select placeholder="选择供应商" showSearch optionFilterProp="label" options={suppliers} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="belongClientId" label="客户" rules={[{ required: true, message: '请选择客户' }]} style={{ flex: 2 }}>
              <Select placeholder="选择客户" showSearch optionFilterProp="label" options={customers} style={{ width: '100%' }} />
            </Form.Item>
          </Space>
          <Form.Item name="batchRemark" label="批次备注">
            <Input.TextArea placeholder="批次备注信息（可选）" rows={2} />
          </Form.Item>
        </Form>

        <h4 style={{ marginBottom: 8 }}>箱号列表</h4>
        <Table
          size="small"
          pagination={false}
          dataSource={cartonRows.map((row, i) => ({ ...row, key: i }))}
          columns={[
            { title: '序号', width: 60, render: (_, __, index) => index + 1 },
            {
              title: '箱号',
              dataIndex: 'cartonId',
              render: (value, _, index) => (
                <Input
                  placeholder="箱号"
                  value={value}
                  onChange={(e) => onCartonIdChange(index, e.target.value)}
                />
              ),
            },
          ]}
          style={{ marginBottom: 16 }}
        />

        <h4 style={{ marginBottom: 8 }}>每箱产品（可选）</h4>
        <Collapse
          items={cartonRows.map((row, boxIndex) => ({
            key: String(boxIndex),
            label: `第 ${boxIndex + 1} 箱 · ${row.cartonId || '（未生成箱号）'}`,
            children: (
              <div>
                <Select
                  placeholder="从已有产品中选择并添加"
                  allowClear
                  showSearch
                  loading={productsLoading}
                  optionFilterProp="label"
                  style={{ width: '100%', marginBottom: 8 }}
                  onChange={(key) => {
                    if (!key) return;
                    const { nameEN, hscode } = parseProductKey(key);
                    const p = productOptions.find((o) => o.nameEN === nameEN && o.hscode === hscode);
                    if (p) addProductToBox(boxIndex, p);
                  }}
                  options={productOptions
                    .filter((p) => p.hscode && p.nameEN)
                    .map((p) => ({
                      value: productKey(p.nameEN, p.hscode),
                      label: `${p.hscode} ${p.nameZN || p.nameEN || ''}`.trim(),
                    }))}
                />
                {row.products.length === 0 ? (
                  <div style={{ color: '#999', fontSize: 12 }}>暂无产品，可不填</div>
                ) : (
                  row.products.map((p, pi) => (
                    <Space key={`${productKey(p.nameEN, p.hscode)}-${pi}`} style={{ marginBottom: 8 }}>
                      <Input value={`${p.hscode}${p.nameEN ? ` / ${p.nameEN}` : ''}`} disabled style={{ width: 220 }} />
                      <InputNumber
                        min={1}
                        value={p.amount ?? 1}
                        onChange={(v) => setProductAmount(boxIndex, pi, v ?? 1)}
                        style={{ width: 80 }}
                      />
                      <Button type="link" danger size="small" onClick={() => removeProductFromBox(boxIndex, pi)}>删除</Button>
                    </Space>
                  ))
                )}
              </div>
            ),
          }))}
        />

        <Space style={{ marginTop: 16 }}>
          <Button type="primary" loading={submitting} onClick={submitAdd}>确认录入</Button>
          <Button onClick={() => setAddModalOpen(false)}>取消</Button>
        </Space>
      </Modal>

      <Modal
        title={receiveTargetIds.length > 1 ? `批量收货（${receiveTargetIds.length} 箱）` : '收货'}
        open={receiveModalOpen}
        onCancel={closeReceiveModal}
        footer={[
          <Button key="cancel" onClick={closeReceiveModal}>取消</Button>,
          <Button key="submit" type="primary" loading={receiveSubmitting} onClick={submitReceive}>确认收货</Button>,
        ]}
        width={560}
        destroyOnClose
      >
        <Form form={receiveForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="arrivedBy" label="到达方式" rules={[{ required: true, message: '请选择到达方式' }]}>
            <Select
              placeholder="请选择"
              options={[
                { value: 0, label: '货拉拉' },
                { value: 1, label: '快递' },
              ]}
            />
          </Form.Item>
          <Form.Item name="queryParamOfShippment" label="查询单号">
            <Input placeholder="货拉拉或快递单号" />
          </Form.Item>
          <Form.Item label="照片">
            {receivePictures.map((p, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                <Input
                  placeholder="图片标注，如货拉拉收货照片"
                  value={p.desc}
                  onChange={(e) => setReceivePictureDesc(idx, e.target.value)}
                  style={{ width: 180 }}
                />
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    const reader = new FileReader();
                    reader.onload = () => {
                      const data = reader.result as string;
                      setReceivePictureBase64(idx, data);
                    };
                    reader.readAsDataURL(file);
                    return false;
                  }}
                >
                  <Button size="small">{p.base64 ? '更换图片' : '选择图片'}</Button>
                </Upload>
                {p.base64 && <span style={{ color: '#52c41a', fontSize: 12 }}>已选</span>}
                <Button type="link" danger size="small" onClick={() => removeReceivePicture(idx)}>删除</Button>
              </div>
            ))}
            <Button type="dashed" size="small" onClick={addReceivePicture}>+ 添加照片</Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`批量改供应商（${supplierTargetIds.length} 箱）`}
        open={supplierModalOpen}
        onCancel={closeSupplierModal}
        footer={[
          <Button key="cancel" onClick={closeSupplierModal}>取消</Button>,
          <Button key="submit" type="primary" loading={supplierSubmitting} onClick={submitBatchUpdateSupplier}>确认更新</Button>,
        ]}
        width={480}
        destroyOnClose
      >
        <Form form={supplierForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="fromSupplierId" label="供应商" rules={[{ required: true, message: '请选择供应商' }]}>
            <Select placeholder="选择供应商" showSearch optionFilterProp="label" options={suppliers} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <CartonDetailDrawer
        open={detailDrawerOpen}
        cartonId={detailCartonId}
        onClose={closeDetail}
        onSaved={() => load()}
      />
    </>
  );
};

export default CartonList;
